import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";

import { fetchMyClubs } from "../../services/clubsApi";
import { useAuth } from "../../context/AuthContext";
import { useCreateClubModal } from "../../components/Clubs/createClub/CreateClubModalContext";

function resolveImageUrl(url) {
  if (!url) return null;
  let s = String(url).trim();
  if (!s) return null;
  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^https?:\/\//i.test(s)) return s;

  const r2Base = (process.env.REACT_APP_R2_PUBLIC_BASE || "").trim();
  if (r2Base && /^clubs\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }

  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = (process.env.REACT_APP_API_BASE || "").trim();
  if (apiBase) return `${apiBase.replace(/\/+$/, "")}${rooted}`;
  return rooted;
}

export default function MyClubsPage() {
  const { user } = useAuth();
  const { openCreateClubModal } = useCreateClubModal();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) {
      setClubs([]);
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setErr("");

    fetchMyClubs()
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data?.clubs) ? data.clubs : [];
        setClubs(list);
      })
      .catch((e) => alive && setErr(e?.message || "Failed to load your clubs"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [user]);

  if (!user) {
    return (
      <div className="relative w-full min-h-screen overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-emerald-400/10 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center">
          <h1 className="text-3xl font-semibold text-white">My Clubs</h1>
          <p className="mt-3 text-white/60 text-sm max-w-md mx-auto">
            Log in to see clubs you have joined.
          </p>
          <Link
            to="/login"
            state={{ from: "/clubs/my-clubs" }}
            className="mt-8 inline-flex h-11 items-center justify-center rounded-full border border-emerald-300/30 bg-emerald-400/12 px-6 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/16 transition"
          >
            Log in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-[520px] w-[520px] rounded-full bg-indigo-400/10 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-16">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <Link
            to="/clubs/discover"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition w-fit"
          >
            <ArrowLeft className="h-4 w-4" />
            Discover
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-white text-3xl sm:text-4xl font-semibold tracking-tight">
              My Clubs
            </h1>
            <p className="mt-2 text-white/55 text-sm max-w-xl">
              Communities you belong to on AnimeHouse.
            </p>
          </div>

          <button
            type="button"
            onClick={() => openCreateClubModal()}
            className="inline-flex h-11 items-center justify-center rounded-full border border-emerald-300/25 bg-emerald-400/10 px-5 text-xs font-semibold tracking-wide uppercase text-white/90 hover:bg-emerald-400/14 transition"
          >
            Create club
          </button>
        </div>

        {err ? (
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-5 py-4 text-red-200 text-sm">
            {err}
          </div>
        ) : null}

        {loading ? (
          <div className="text-white/50 text-sm py-12">Loading your clubs…</div>
        ) : clubs.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] px-8 py-12 text-center">
            <h2 className="text-xl font-semibold text-white">No clubs yet</h2>
            <p className="mt-2 text-white/55 text-sm max-w-md mx-auto">
              Join a club from Discover, or start your own.
            </p>
            <Link
              to="/clubs/discover"
              className="mt-6 inline-flex h-11 items-center justify-center rounded-full border border-white/14 bg-white/[0.05] px-6 text-sm font-medium text-white/90 hover:bg-white/[0.08] transition"
            >
              Browse clubs
            </Link>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {clubs.map((c) => {
              const slug = c?.slug;
              const banner = resolveImageUrl(
                c?.cover_image_url || c?.banner_image_url || c?.banner_url
              );
              const members = Number(c?.member_count ?? c?.members ?? 0);
              const role = String(c?.role || "").toLowerCase();

              return (
                <Link
                  key={c?.club_id ?? c?.id ?? slug}
                  to={
                    slug
                      ? `/clubs/${encodeURIComponent(slug)}`
                      : "/clubs/discover"
                  }
                  className="group rounded-3xl border border-white/10 bg-[#0B0F16]/80 backdrop-blur-md overflow-hidden hover:border-white/18 transition"
                >
                  <div className="h-36 w-full bg-white/5 overflow-hidden">
                    {banner ? (
                      <img
                        src={banner}
                        alt=""
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-white/30 text-sm">
                        No banner
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <h2 className="text-lg font-semibold text-white truncate">
                        {c?.name || "Club"}
                      </h2>
                      {role === "owner" ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wider text-emerald-300/90 border border-emerald-400/25 rounded-full px-2 py-0.5">
                          Owner
                        </span>
                      ) : null}
                    </div>
                    {c?.description ? (
                      <p className="mt-2 text-sm text-white/55 line-clamp-2">
                        {c.description}
                      </p>
                    ) : null}
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-white/50">
                      <Users className="h-4 w-4" />
                      {members.toLocaleString()} members
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
